import {
    Checkbox,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    styled,
} from '@mui/material';
import Button from '@shared/components/core/button/CoreButton';
import { ListDescription, ListFieldset, ListLabel } from '@shared/components/core/field/list/CommonListFieldStyles';
import { MacroEventType, macroEventTypes } from '@shared/models/deviceProfiles/MacroEvent';
import DeviceTypeThrottleRules, {
    IncompatibleActionGroup,
    MacroActionReference,
    RequiredPauseBetweenRule,
    getDefaultThrottleRules,
} from '@shared/models/devices/DeviceTypeThrottleRules';
import { useTranslation } from 'react-i18next';

export interface IDeviceTypeThrottleRulesEditorProps {
    throttleRules?: DeviceTypeThrottleRules;
    onUpdate: (rules: DeviceTypeThrottleRules) => void;
}

const createDefaultActionRef = (): MacroActionReference => ({
    eventType: 'KeyPress',
    key: '',
    deviceTypeCustomActionId: undefined,
});

export default function DeviceTypeThrottleRulesEditor({
    throttleRules,
    onUpdate,
}: IDeviceTypeThrottleRulesEditorProps) {
    const { t } = useTranslation();

    const currentRules: DeviceTypeThrottleRules = throttleRules ?? getDefaultThrottleRules();

    const handleBooleanChange = (field: keyof DeviceTypeThrottleRules, value: boolean) => {
        onUpdate({
            ...currentRules,
            [field]: value,
        });
    };

    const handleNumberChange = (field: keyof DeviceTypeThrottleRules, value: string) => {
        if (value === '') {
            onUpdate({
                ...currentRules,
                [field]: undefined,
            });
            return;
        }
        const numValue = parseInt(value, 10);
        if (!isNaN(numValue) && numValue >= 0) {
            onUpdate({
                ...currentRules,
                [field]: numValue,
            });
        }
    };

    // --- Incompatible Action Groups ---
    const incompatibleGroups = currentRules.incompatibleActionGroups ?? [];

    const addIncompatibleGroup = () => {
        onUpdate({
            ...currentRules,
            incompatibleActionGroups: [
                ...incompatibleGroups,
                { actions: [createDefaultActionRef(), createDefaultActionRef()], reason: '' },
            ],
        });
    };

    const removeIncompatibleGroup = (index: number) => {
        const updated = incompatibleGroups.filter((_, i) => i !== index);
        onUpdate({
            ...currentRules,
            incompatibleActionGroups: updated.length > 0 ? updated : undefined,
        });
    };

    const updateIncompatibleGroup = (index: number, group: IncompatibleActionGroup) => {
        const updated = [...incompatibleGroups];
        updated[index] = group;
        onUpdate({ ...currentRules, incompatibleActionGroups: updated });
    };

    // --- Required Pause Between Rules ---
    const pauseRules = currentRules.requiredPauseBetweenRules ?? [];

    const addPauseRule = () => {
        onUpdate({
            ...currentRules,
            requiredPauseBetweenRules: [
                ...pauseRules,
                {
                    actionA: createDefaultActionRef(),
                    actionB: createDefaultActionRef(),
                    minimumPauseMilliseconds: 100,
                    bidirectional: true,
                },
            ],
        });
    };

    const removePauseRule = (index: number) => {
        const updated = pauseRules.filter((_, i) => i !== index);
        onUpdate({
            ...currentRules,
            requiredPauseBetweenRules: updated.length > 0 ? updated : undefined,
        });
    };

    const updatePauseRule = (index: number, rule: RequiredPauseBetweenRule) => {
        const updated = [...pauseRules];
        updated[index] = rule;
        onUpdate({ ...currentRules, requiredPauseBetweenRules: updated });
    };

    return (
        <ListFieldset>
            <ListLabel as="legend">{t('common:organization.projects.deviceType.throttleRules.title')}</ListLabel>
            <ListDescription>{t('common:organization.projects.deviceType.throttleRules.description')}</ListDescription>

            {/* Input Disable Checkboxes */}
            <StyledFormGroup>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={currentRules.disableVoiceInput}
                            onChange={(e) => handleBooleanChange('disableVoiceInput', e.target.checked)}
                        />
                    }
                    label={t('common:organization.projects.deviceType.throttleRules.disableVoiceInput')}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={currentRules.disableHotkeyInput}
                            onChange={(e) => handleBooleanChange('disableHotkeyInput', e.target.checked)}
                        />
                    }
                    label={t('common:organization.projects.deviceType.throttleRules.disableHotkeyInput')}
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={currentRules.disableCameraInput}
                            onChange={(e) => handleBooleanChange('disableCameraInput', e.target.checked)}
                        />
                    }
                    label={t('common:organization.projects.deviceType.throttleRules.disableCameraInput')}
                />
            </StyledFormGroup>

            {/* Rate Limit & Macro Length Fields */}
            <NumberInputsContainer>
                <TextField
                    type="number"
                    label={t('common:organization.projects.deviceType.throttleRules.maximumCommandsPerSecond')}
                    value={currentRules.maximumCommandsPerSecond ?? ''}
                    onChange={(e) => handleNumberChange('maximumCommandsPerSecond', e.target.value)}
                    slotProps={{ htmlInput: { min: 0 } }}
                    fullWidth
                />
                <TextField
                    type="number"
                    label={t('common:organization.projects.deviceType.throttleRules.maximumCommandsPerMinute')}
                    value={currentRules.maximumCommandsPerMinute ?? ''}
                    onChange={(e) => handleNumberChange('maximumCommandsPerMinute', e.target.value)}
                    slotProps={{ htmlInput: { min: 0 } }}
                    fullWidth
                />
                <TextField
                    type="number"
                    label={t('common:organization.projects.deviceType.throttleRules.maximumMacroLength')}
                    value={currentRules.maximumMacroLength ?? ''}
                    onChange={(e) => handleNumberChange('maximumMacroLength', e.target.value)}
                    slotProps={{ htmlInput: { min: 0 } }}
                    fullWidth
                />
            </NumberInputsContainer>

            <StyledDivider />

            {/* Jitter Settings */}
            <SectionHeader variant="subtitle2">
                {t('common:organization.projects.deviceType.throttleRules.jitter.title')}
            </SectionHeader>
            <ListDescription>
                {t('common:organization.projects.deviceType.throttleRules.jitter.description')}
            </ListDescription>
            <NumberInputsContainer>
                <TextField
                    type="number"
                    label={t('common:organization.projects.deviceType.throttleRules.jitter.holdTimeJitterMin')}
                    value={currentRules.holdTimeJitterMinMilliseconds ?? ''}
                    onChange={(e) => handleNumberChange('holdTimeJitterMinMilliseconds', e.target.value)}
                    slotProps={{ htmlInput: { min: 0 } }}
                    fullWidth
                />
                <TextField
                    type="number"
                    label={t('common:organization.projects.deviceType.throttleRules.jitter.holdTimeJitterMax')}
                    value={currentRules.holdTimeJitterMaxMilliseconds ?? ''}
                    onChange={(e) => handleNumberChange('holdTimeJitterMaxMilliseconds', e.target.value)}
                    slotProps={{ htmlInput: { min: 0 } }}
                    fullWidth
                />
                <TextField
                    type="number"
                    label={t('common:organization.projects.deviceType.throttleRules.jitter.pauseTimeJitterMin')}
                    value={currentRules.pauseTimeJitterMinMilliseconds ?? ''}
                    onChange={(e) => handleNumberChange('pauseTimeJitterMinMilliseconds', e.target.value)}
                    slotProps={{ htmlInput: { min: 0 } }}
                    fullWidth
                />
                <TextField
                    type="number"
                    label={t('common:organization.projects.deviceType.throttleRules.jitter.pauseTimeJitterMax')}
                    value={currentRules.pauseTimeJitterMaxMilliseconds ?? ''}
                    onChange={(e) => handleNumberChange('pauseTimeJitterMaxMilliseconds', e.target.value)}
                    slotProps={{ htmlInput: { min: 0 } }}
                    fullWidth
                />
            </NumberInputsContainer>

            <StyledDivider />

            {/* Incompatible Action Groups */}
            <SectionHeader variant="subtitle2">
                {t('common:organization.projects.deviceType.throttleRules.incompatibleActions.title')}
            </SectionHeader>
            <ListDescription>
                {t('common:organization.projects.deviceType.throttleRules.incompatibleActions.description')}
            </ListDescription>

            {incompatibleGroups.map((group, groupIndex) => (
                <GroupCard key={groupIndex}>
                    <GroupHeader>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Group {groupIndex + 1}
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={() => removeIncompatibleGroup(groupIndex)}
                            aria-label={t(
                                'common:organization.projects.deviceType.throttleRules.incompatibleActions.removeGroup'
                            )}>
                            x
                        </IconButton>
                    </GroupHeader>
                    <TextField
                        label={t('common:organization.projects.deviceType.throttleRules.incompatibleActions.reason')}
                        value={group.reason ?? ''}
                        onChange={(e) =>
                            updateIncompatibleGroup(groupIndex, { ...group, reason: e.target.value || undefined })
                        }
                        placeholder={t(
                            'common:organization.projects.deviceType.throttleRules.incompatibleActions.reasonPlaceholder'
                        )}
                        fullWidth
                        size="small"
                        sx={{ marginBottom: '12px' }}
                    />
                    {group.actions.map((action, actionIndex) => (
                        <ActionRefRow key={actionIndex}>
                            <MacroActionReferenceEditor
                                action={action}
                                onUpdate={(updated) => {
                                    const newActions = [...group.actions];
                                    newActions[actionIndex] = updated;
                                    updateIncompatibleGroup(groupIndex, { ...group, actions: newActions });
                                }}
                            />
                            <IconButton
                                size="small"
                                disabled={group.actions.length <= 2}
                                onClick={() => {
                                    const newActions = group.actions.filter((_, i) => i !== actionIndex);
                                    updateIncompatibleGroup(groupIndex, { ...group, actions: newActions });
                                }}
                                aria-label={t(
                                    'common:organization.projects.deviceType.throttleRules.incompatibleActions.removeAction'
                                )}>
                                x
                            </IconButton>
                        </ActionRefRow>
                    ))}
                    <Button
                        $color="solidAccent"
                        $size="small"
                        onClick={() => {
                            updateIncompatibleGroup(groupIndex, {
                                ...group,
                                actions: [...group.actions, createDefaultActionRef()],
                            });
                        }}>
                        {t('common:organization.projects.deviceType.throttleRules.incompatibleActions.addAction')}
                    </Button>
                </GroupCard>
            ))}
            <Button $color="solidAccent" $size="small" onClick={addIncompatibleGroup}>
                {t('common:organization.projects.deviceType.throttleRules.incompatibleActions.addGroup')}
            </Button>

            <StyledDivider />

            {/* Required Pause Between Rules */}
            <SectionHeader variant="subtitle2">
                {t('common:organization.projects.deviceType.throttleRules.requiredPause.title')}
            </SectionHeader>
            <ListDescription>
                {t('common:organization.projects.deviceType.throttleRules.requiredPause.description')}
            </ListDescription>

            {pauseRules.map((rule, ruleIndex) => (
                <GroupCard key={ruleIndex}>
                    <GroupHeader>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Rule {ruleIndex + 1}
                        </Typography>
                        <IconButton
                            size="small"
                            onClick={() => removePauseRule(ruleIndex)}
                            aria-label={t(
                                'common:organization.projects.deviceType.throttleRules.requiredPause.removeRule'
                            )}>
                            x
                        </IconButton>
                    </GroupHeader>
                    <PauseRuleRow>
                        <ActionRefSection>
                            <Typography variant="caption">
                                {t('common:organization.projects.deviceType.throttleRules.requiredPause.actionA')}
                            </Typography>
                            <MacroActionReferenceEditor
                                action={rule.actionA}
                                onUpdate={(updated) => updatePauseRule(ruleIndex, { ...rule, actionA: updated })}
                            />
                        </ActionRefSection>
                        <ActionRefSection>
                            <Typography variant="caption">
                                {t('common:organization.projects.deviceType.throttleRules.requiredPause.actionB')}
                            </Typography>
                            <MacroActionReferenceEditor
                                action={rule.actionB}
                                onUpdate={(updated) => updatePauseRule(ruleIndex, { ...rule, actionB: updated })}
                            />
                        </ActionRefSection>
                    </PauseRuleRow>
                    <PauseRuleOptionsRow>
                        <TextField
                            type="number"
                            label={t(
                                'common:organization.projects.deviceType.throttleRules.requiredPause.minimumPause'
                            )}
                            value={rule.minimumPauseMilliseconds}
                            onChange={(e) => {
                                const val = parseInt(e.target.value, 10);
                                if (!isNaN(val) && val >= 0) {
                                    updatePauseRule(ruleIndex, { ...rule, minimumPauseMilliseconds: val });
                                }
                            }}
                            slotProps={{ htmlInput: { min: 0 } }}
                            size="small"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={rule.bidirectional}
                                    onChange={(e) =>
                                        updatePauseRule(ruleIndex, { ...rule, bidirectional: e.target.checked })
                                    }
                                />
                            }
                            label={t(
                                'common:organization.projects.deviceType.throttleRules.requiredPause.bidirectional'
                            )}
                        />
                    </PauseRuleOptionsRow>
                </GroupCard>
            ))}
            <Button $color="solidAccent" $size="small" onClick={addPauseRule}>
                {t('common:organization.projects.deviceType.throttleRules.requiredPause.addRule')}
            </Button>
        </ListFieldset>
    );
}

// --- Inline sub-editor for MacroActionReference ---

interface MacroActionReferenceEditorProps {
    action: MacroActionReference;
    onUpdate: (action: MacroActionReference) => void;
}

function MacroActionReferenceEditor({ action, onUpdate }: MacroActionReferenceEditorProps) {
    const { t } = useTranslation();
    const isCustomAction = action.eventType === 'DeviceTypeCustomAction';

    return (
        <ActionRefFields>
            <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>
                    {t('common:organization.projects.deviceType.throttleRules.incompatibleActions.eventType')}
                </InputLabel>
                <Select
                    value={action.eventType}
                    label={t('common:organization.projects.deviceType.throttleRules.incompatibleActions.eventType')}
                    onChange={(e) =>
                        onUpdate({
                            ...action,
                            eventType: e.target.value as MacroEventType,
                            key: e.target.value === 'DeviceTypeCustomAction' ? undefined : action.key,
                            deviceTypeCustomActionId:
                                e.target.value === 'DeviceTypeCustomAction'
                                    ? action.deviceTypeCustomActionId
                                    : undefined,
                        })
                    }>
                    {macroEventTypes.map((evt) => (
                        <MenuItem key={evt.eventType} value={evt.eventType}>
                            {t(evt.displayName)}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            {isCustomAction ? (
                <TextField
                    label={t(
                        'common:organization.projects.deviceType.throttleRules.incompatibleActions.customActionId'
                    )}
                    value={action.deviceTypeCustomActionId ?? ''}
                    onChange={(e) => onUpdate({ ...action, deviceTypeCustomActionId: e.target.value || undefined })}
                    size="small"
                />
            ) : (
                <TextField
                    label={t('common:organization.projects.deviceType.throttleRules.incompatibleActions.key')}
                    value={action.key ?? ''}
                    onChange={(e) => onUpdate({ ...action, key: e.target.value || undefined })}
                    size="small"
                />
            )}
        </ActionRefFields>
    );
}

// --- Styled components ---

const StyledFormGroup = styled(FormGroup)`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    width: 100%;
    margin-bottom: 16px;
`;

const NumberInputsContainer = styled('div')`
    display: grid;
    padding-top: 0.5rem;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 16px;
    width: 100%;
`;

const StyledDivider = styled(Divider)`
    width: 100%;
    margin: 24px 0;
`;

const SectionHeader = styled(Typography)`
    align-self: flex-start;
    margin-bottom: 4px;
`;

const GroupCard = styled('div')`
    width: 100%;
    border: 1px solid ${({ theme }) => theme.palette.divider};
    border-radius: 8px;
    padding: 16px;
    margin: 8px 0;
`;

const GroupHeader = styled('div')`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
`;

const ActionRefRow = styled('div')`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
`;

const ActionRefFields = styled('div')`
    display: flex;
    gap: 8px;
    flex: 1;
`;

const PauseRuleRow = styled('div')`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    margin-bottom: 12px;
`;

const ActionRefSection = styled('div')`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const PauseRuleOptionsRow = styled('div')`
    display: flex;
    gap: 16px;
    align-items: center;
`;
